/**
 * Cookieless, IP-free pageview tracker.
 *
 * One random `session_token` per browser session (kept in sessionStorage, so
 * it's wiped when the tab closes). Used purely to de-duplicate unique
 * sessions in the analytics dashboard — never linked to a user identity.
 *
 * Drop <UsageTracker /> once near the top of <App />.
 */
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

const STORAGE_KEY = "kult_session_token";

function getSessionToken() {
  let t = sessionStorage.getItem(STORAGE_KEY);
  if (!t) {
    t = "s_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    sessionStorage.setItem(STORAGE_KEY, t);
  }
  return t;
}

function send(payload) {
  // Use sendBeacon when available — survives page unload without delaying nav.
  const url = `${process.env.REACT_APP_BACKEND_URL}/api/analytics/track`;
  const body = JSON.stringify(payload);
  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon(url, blob);
      return;
    }
  } catch (_e) {}
  // Fallback — fire-and-forget fetch, ignore errors entirely.
  fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true }).catch(() => {});
}

export function UsageTracker() {
  const loc = useLocation();
  const lastPath = useRef("");

  // Ignore admin/portal/checkout-success — we only want public storefront pages.
  useEffect(() => {
    const path = loc.pathname || "/";
    if (path === lastPath.current) return;
    lastPath.current = path;

    // Don't track admin/internal navigation.
    if (path.startsWith("/portal") || path.startsWith("/checkout/success")) return;

    const isProduct = path.startsWith("/products/");
    send({
      path,
      session_token: getSessionToken(),
      referrer: document.referrer || null,
      event_type: isProduct ? "product_view" : "pageview",
      product_id: isProduct ? path.split("/products/")[1].split("/")[0] : null,
    });
  }, [loc.pathname]);

  return null;
}
